import { useState, useEffect, useMemo } from "react";
import { DateTime } from "luxon";

export const useChartData = (
  rawData,
  selectedMetrics,
  dataConfig,
  timeRange,
) => {
  const [processedData, setProcessedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Process raw data into chart-ready format
  const processChartData = useMemo(() => {
    console.log("useChartData Debug - Input data:", {
      hasRawData: !!rawData,
      selectedMetricsCount: selectedMetrics.length,
      selectedMetrics,
      hasDataConfig: !!dataConfig,
      rawDataStructure: rawData ? Object.keys(rawData) : [],
      dataConfigStructure: dataConfig
        ? Array.isArray(dataConfig)
          ? `Array[${dataConfig.length}]`
          : typeof dataConfig
        : "undefined",
    });

    if (!rawData || !selectedMetrics.length || !dataConfig) {
      console.log("useChartData Debug - Missing required data:", {
        rawData: !!rawData,
        selectedMetricsLength: selectedMetrics.length,
        dataConfig: !!dataConfig,
      });
      return [];
    }

    try {
      const chartSeries = [];
      // Handle the actual data structure from SystemFC
      const dataTable = rawData?.dataTable || rawData?.tableData || rawData;
      // Handle the actual config structure
      const configData = dataConfig?.[0]?.dataLabels || dataConfig;
      // Get chartConfig for unit information
      const chartConfig = rawData?.chartConfig || [];

      console.log("useChartData Debug - Processing structures:", {
        dataTableLength: Array.isArray(dataTable)
          ? dataTable.length
          : "not array",
        dataTableSample:
          Array.isArray(dataTable) && dataTable.length > 0
            ? Object.keys(dataTable[0])
            : "no data",
        configDataLength: Array.isArray(configData)
          ? configData.length
          : "not array",
        configDataSample:
          Array.isArray(configData) && configData.length > 0
            ? configData.slice(0, 3)
            : "no config",
      });

      selectedMetrics.forEach((metricKey, index) => {
        // Find the metric configuration
        const metricConfig = configData.find((config) => {
          // Handle both pgColumn and pgcolumn cases
          const pgColumn = config.pgColumn || config.pgcolumn;
          const fields = metricKey.split("_");
          return (
            pgColumn === metricKey ||
            fields.every((field) => pgColumn === field) ||
            pgColumn === fields[0]
          );
        });

        console.log("useChartData Debug - Metric processing:", {
          metricKey,
          foundConfig: !!metricConfig,
          configLabel: metricConfig?.uiLabel || metricConfig?.uilabel,
        });

        if (!metricConfig) {
          console.warn(
            "useChartData Debug - No config found for metric:",
            metricKey,
          );
          return;
        }

        // Extract data points for this metric
        const dataPoints = dataTable
          .map((row) => {
            let timestamp = null;
            let value = null;

            // Handle timestamp - try different possible column names
            const timeFields = [
              "capture_datetime",
              "timestamp",
              "date",
              "time",
            ];
            for (const field of timeFields) {
              if (row[field]) {
                timestamp = new Date(row[field]);
                break;
              }
            }

            // Handle value extraction
            const pgColumn = metricConfig.pgColumn || metricConfig.pgcolumn;
            value = parseFloat(row[metricKey]) || parseFloat(row[pgColumn]);

            // Filter by time range if specified
            if (timeRange && timestamp) {
              const now = DateTime.now();
              const dataTime = DateTime.fromJSDate(timestamp);

              switch (timeRange) {
                case "1h":
                  if (now.diff(dataTime, "hours").hours > 1) return null;
                  break;
                case "24h":
                  if (now.diff(dataTime, "hours").hours > 24) return null;
                  break;
                case "7d":
                  if (now.diff(dataTime, "days").days > 7) return null;
                  break;
                case "30d":
                  if (now.diff(dataTime, "days").days > 30) return null;
                  break;
              }
            }

            return timestamp && value !== null
              ? {
                  x: timestamp,
                  y: value,
                  label: metricConfig.uilabel || metricKey,
                }
              : null;
          })
          .filter((point) => point !== null);

        if (dataPoints.length > 0) {
          // Extract unit from data - units are typically stored as fieldname_units
          // e.g., temp_probe_0_value has units in temp_probe_0_units
          let extractedUnit = metricConfig.unit || "";

          if (!extractedUnit && chartConfig.length > 0) {
            // Look for unit in chartConfig using pgcolumn field
            const pgColumn = metricConfig.pgColumn || metricConfig.pgcolumn;
            const chartConfigEntry = chartConfig.find(
              (config) =>
                config.pgcolumn === pgColumn || config.pgColumn === pgColumn,
            );

            if (chartConfigEntry && chartConfigEntry.unit) {
              extractedUnit = chartConfigEntry.unit;
              console.log(
                `useChartData Debug - Extracted unit for ${metricKey}: "${extractedUnit}" from chartConfig`,
              );
            } else {
              console.log(
                `useChartData Debug - No unit found in chartConfig for ${metricKey} (pgColumn: ${pgColumn})`,
              );
            }
          }

          chartSeries.push({
            name: metricConfig.uiLabel || metricConfig.uilabel || metricKey,
            key: metricKey,
            data: dataPoints,
            type: metricConfig.type || "continuous",
            unit: extractedUnit,
            color: getMetricColor(index),
            yAxisID: getYAxisId(extractedUnit || metricKey),
          });
        }
      });

      return chartSeries;
    } catch (err) {
      console.error("Error processing chart data:", err);
      setError(err.message);
      return [];
    }
  }, [rawData, selectedMetrics, dataConfig, timeRange]);

  useEffect(() => {
    setProcessedData(processChartData);
  }, [processChartData]);

  // Get unique Y-axis configurations
  const yAxisConfig = useMemo(() => {
    const axes = {};

    processedData.forEach((series) => {
      if (!axes[series.yAxisID]) {
        axes[series.yAxisID] = {
          id: series.yAxisID,
          label: "", // No unit label on y-axis
          unit: "", // No unit
          series: [],
        };
      }
      axes[series.yAxisID].series.push(series.key);
    });

    return Object.values(axes);
  }, [processedData]);

  // Calculate data statistics
  const dataStats = useMemo(() => {
    if (!processedData.length) return null;

    const stats = {};
    processedData.forEach((series) => {
      const values = series.data.map((point) => point.y);
      stats[series.key] = {
        min: Math.min(...values),
        max: Math.max(...values),
        avg: values.reduce((sum, v) => sum + v, 0) / values.length,
        count: values.length,
        latest: values[values.length - 1],
      };
    });

    return stats;
  }, [processedData]);

  return {
    chartData: processedData,
    yAxisConfig,
    dataStats,
    loading,
    error,
  };
};

// Helper function to generate colors for metrics
const getMetricColor = (index) => {
  const colors = [
    "#2c5aa0", // Primary blue
    "#27ae60", // Green
    "#e74c3c", // Red
    "#f5a623", // Orange
    "#9b59b6", // Purple
    "#3498db", // Light blue
    "#e67e22", // Dark orange
    "#1abc9c", // Teal
    "#34495e", // Dark gray
    "#95a5a6", // Gray
  ];

  return colors[index % colors.length];
};

// Helper function to group metrics by unit for Y-axis assignment
const getYAxisId = (unit) => {
  // Ensure unit is a string
  const unitStr = unit != null ? String(unit) : "";

  const unitMappings = {
    "%": "percentage",
    "°C": "temperature",
    "°F": "temperature",
    F: "temperature",
    C: "temperature",
    psia: "pressure",
    mbar: "pressure",
    bar: "pressure",
    kPa: "pressure",
    V: "voltage",
    A: "current",
    W: "power",
    GPM: "flow",
    LPM: "flow",
    "": "dimensionless",
  };

  // Return single axis ID for all metrics (no unit-based grouping)
  return "primary";
};
